import { Institution } from '../entities/institution.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getInstitutionsByIDs = registerQuery({
  operationName: 'getInstitutionsByIDs',
  type: () => [Institution],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Institution, ids, gqlFields),
});

export const subscribeInstitutionsByIDs = registerSubscription({
  operationName: 'subscribeInstitutionsByIDs',
  type: () => [Institution],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Institution, ids, gqlFields),
});

export const getAllInstitutions = registerQuery({
  operationName: 'getAllInstitutions',
  type: () => [Institution],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) =>
      readAllEntities(Institution, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllInstitutions = registerSubscription({
  operationName: 'subscribeAllInstitutions',
  type: () => [Institution],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) =>
      watchAllEntities(Institution, gqlFields, { skip, limit, excludeIDs }),
});

export const getInstitutionByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getInstitutionsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeInstitutionByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Institution>>(gqlFields: SelSet) =>
    id ? subscribeInstitutionsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);

// A zero MongoDB limit counts the full visible set rather than the default first 1,000.
export const countInstitutions = registerQuery({
  operationName: 'countInstitutions',
  type: () => Int,
  params: [],
  resolve: () => () => readAllEntities(Institution, { _id: 1 }, { limit: 0 }).then(entities => entities.length),
});
